import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../utils/apiClient';

interface Church {
  id: number;
  name: string;
  address?: string;
  is_primary: boolean;
}

interface ChurchServiceSelection {
  churchId: number;
  serviceDefId: number;
}

interface ChurchContextValue {
  currentChurch: Church | null;
  userChurches: Church[];
  isLoadingChurches: boolean;
  selectedServices?: ChurchServiceSelection[];
  setSelectedServices?: (selections: ChurchServiceSelection[]) => void;
  switchChurch: (churchId: number) => Promise<void>;
  refreshChurches: () => Promise<void>;
}

const ChurchContext = createContext<ChurchContextValue | null>(null);

export const useChurch = () => {
  const context = useContext(ChurchContext);
  if (!context) {
    throw new Error('useChurch must be used within a ChurchProvider');
  }
  return context;
};

interface ChurchProviderProps {
  children: ReactNode;
}

export const ChurchProvider: React.FC<ChurchProviderProps> = ({ children }) => {
  const [currentChurch, setCurrentChurch] = useState<Church | null>(null);
  const [userChurches, setUserChurches] = useState<Church[]>([]);
  const [isLoadingChurches, setIsLoadingChurches] = useState(true);
  const [selectedServices, setSelectedServices] = useState<ChurchServiceSelection[]>([]);

  const loadChurches = async () => {
    try {
      setIsLoadingChurches(true);
      console.log('🏛️ Loading churches for user...');

      const response = await apiClient.get('/users/churches');
      const churches = response.data;

      console.log('🏛️ Received churches with is_primary:', churches.map((c: Church) => ({ id: c.id, name: c.name, is_primary: c.is_primary })));
      setUserChurches(churches);

      // Set current church to primary, or first church if no primary
      const primaryChurch = churches.find((c: Church) => c.is_primary);
      const selectedChurch = primaryChurch || churches[0] || null;

      console.log('🏛️ Primary church found:', primaryChurch);
      console.log('🏛️ Setting current church to:', selectedChurch);
      setCurrentChurch(selectedChurch);
    } catch (error) {
      console.error('❌ Failed to load churches:', error);
    } finally {
      setIsLoadingChurches(false);
    }
  };

  const switchChurch = async (churchId: number) => {
    try {
      console.log('🏛️ Switching to church ID:', churchId);

      const response = await apiClient.put(`/users/churches/${churchId}/primary`);
      console.log('🏛️ Church switch API response:', response.data);

      // Refresh churches to get updated primary status
      console.log('🏛️ Reloading churches...');
      await loadChurches();

      console.log('🏛️ Church switch completed, currentChurch is now:', currentChurch);
    } catch (error) {
      console.error('❌ Failed to switch church:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadChurches();
  }, []);

  const value: ChurchContextValue = {
    currentChurch,
    userChurches,
    isLoadingChurches,
    selectedServices,
    setSelectedServices,
    switchChurch,
    refreshChurches: loadChurches,
  };

  return (
    <ChurchContext.Provider value={value}>
      {children}
    </ChurchContext.Provider>
  );
};